import React, { useEffect, useRef, useState, useCallback } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import Cookies from "js-cookie";
import { io } from "socket.io-client";

// Backend (Socket.js) çark sözleşmesi:
//  emit  -> cark_join_room {roomCode, token} | cark_leave_room {roomCode}
//           cark_update_items {roomCode, items, targetWinnerCount, token}   (yalnız host)
//           cark_spin_start {roomCode, winningIndex, targetWinnerCount, currentWinnerCount, winnerName, token} (yalnız host)
//           cark_reset {roomCode, items, token}   (yalnız host)
//  on    -> cark_items_updated {items, targetWinnerCount}
//           cark_room_user_count {userCount}
//           cark_joined_success {roomCode, userCount}
//           cark_spin_started {winningIndex, targetWinnerCount, currentWinnerCount, timestamp}
//           cark_resetted {items}
//           error {message}
// Host = odaya ilk giren (server host_user_id'yi token'dan yazar). Host değilse
// spin/update/reset backend tarafından reddedilir -> "error" event'i döner.

const API = process.env.REACT_APP_API_URL;
const WS = process.env.REACT_APP_API_WEBSOCKET || API || "https://nmuniversalteam.com";

const SPIN_MS = 5000;
const EXTRA_SPINS = 6;

const PALETTE = [
  "#7c3aed", "#db2777", "#0891b2", "#ea580c",
  "#16a34a", "#2563eb", "#d97706", "#dc2626",
  "#0d9488", "#9333ea", "#c026d3", "#65a30d",
];

const CENTER = 160;
const RADIUS = 150;

// "tepeden saat yönünde" açıdan ekran koordinatı
function polar(angleDeg, r) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CENTER + r * Math.sin(rad),
    y: CENTER - r * Math.cos(rad),
  };
}

function genRoomCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < 6; i++) {
    s += chars[Math.floor(Math.random() * chars.length)];
  }
  return s;
}

export default function Cark() {
  const token = Cookies.get("token");

  const [items, setItems] = useState([
    "Ahmet", "Mehmet", "Ayşe", "Fatma", "Ali", "Zeynep",
  ]);
  const [newItem, setNewItem] = useState("");
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // kazanan indexleri
  const [winners, setWinners] = useState([]); // kazanan isimler (sırayla)
  const [targetWinnerCount, setTargetWinnerCount] = useState(1);
  const [resultWinner, setResultWinner] = useState(null);

  // Canlı oda
  const [isRealtime, setIsRealtime] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [roomCode, setRoomCode] = useState("");
  const [userCount, setUserCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [joinInput, setJoinInput] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const socketRef = useRef(null);
  const itemsRef = useRef(items);
  const rotationRef = useRef(0);
  const roomRef = useRef("");
  const joinedOnceRef = useRef(false); // bu odaya en az bir kez katıldık mı
  const pendingWinnerRef = useRef(-1);
  const finalizeTimerRef = useRef(null);

  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => { rotationRef.current = rotation; }, [rotation]);
  useEffect(() => { roomRef.current = roomCode; }, [roomCode]);

  const canControl = !isRealtime || isHost;

  // --- Çark animasyonu (host + izleyici aynı index'e döner) ---
  const animateToIndex = useCallback((winningIndex) => {
    const n = itemsRef.current.length;
    if (winningIndex < 0 || winningIndex >= n) return;
    const seg = 360 / n;
    const center = (winningIndex + 0.5) * seg; // tepeden saat yönünde
    const current = rotationRef.current;
    // current + delta ≡ -center (mod 360)  => pointer (tepe) o dilime denk gelir
    const delta = ((((-center - current) % 360) + 360) % 360);
    const target = current + EXTRA_SPINS * 360 + delta;

    pendingWinnerRef.current = winningIndex;
    setSpinning(true);
    setResultWinner(null);
    // bir sonraki frame'de transition uygulansın
    requestAnimationFrame(() => {
      rotationRef.current = target;
      setRotation(target);
    });

    if (finalizeTimerRef.current) clearTimeout(finalizeTimerRef.current);
    finalizeTimerRef.current = setTimeout(() => {
      const idx = pendingWinnerRef.current;
      const name = itemsRef.current[idx];
      setSpinning(false);
      if (idx >= 0 && name != null) {
        setSelected((prev) => {
          const nx = new Set(prev);
          nx.add(idx);
          return nx;
        });
        setWinners((prev) => [...prev, name]);
        setResultWinner(name);
      }
    }, SPIN_MS + 150);
  }, []);

  // --- Socket kurulumu (yalnız canlı oda için, lazy) ---
  const ensureSocket = useCallback(() => {
    if (socketRef.current) return socketRef.current;
    const socket = io(WS, {
      transports: ["websocket", "polling"],
      autoConnect: true,
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      auth: { token },
      query: { token },
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      // YENİDEN bağlanmada odaya tekrar katıl. İlk bağlanışta katılımı
      // startLiveRoom/joinLiveRoom yapıyor; burada da yollarsak oda iki kez
      // join ediliyor ve ikinci join, host'un listesi daha DB'ye yazılmadan
      // odanın boş listesini geri yayıp host'un çarkını siliyordu.
      if (roomRef.current && joinedOnceRef.current) {
        socket.emit("cark_join_room", { roomCode: roomRef.current, token });
      }
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));

    socket.on("cark_joined_success", (d) => {
      joinedOnceRef.current = true;
      if (d && typeof d.userCount === "number") setUserCount(d.userCount);
    });
    socket.on("cark_room_user_count", (d) => {
      if (d && typeof d.userCount === "number") setUserCount(d.userCount);
    });
    socket.on("cark_items_updated", (d) => {
      if (!d) return;
      if (Array.isArray(d.items)) setItems(d.items);
      if (typeof d.targetWinnerCount === "number") setTargetWinnerCount(d.targetWinnerCount);
    });
    socket.on("cark_spin_started", (d) => {
      if (!d || typeof d.winningIndex !== "number") return;
      if (typeof d.targetWinnerCount === "number") setTargetWinnerCount(d.targetWinnerCount);
      animateToIndex(d.winningIndex);
    });
    socket.on("cark_resetted", (d) => {
      setSelected(new Set());
      setWinners([]);
      setResultWinner(null);
      if (d && Array.isArray(d.items)) setItems(d.items);
    });
    socket.on("error", (e) => {
      const msg = (e && e.message) || "Bir hata oluştu.";
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(""), 4000);
    });

    return socket;
  }, [token, animateToIndex]);

  // giriş guard + cleanup
  useEffect(() => {
    if (!token) {
      window.location.href = "/login";
      return;
    }
    return () => {
      if (finalizeTimerRef.current) clearTimeout(finalizeTimerRef.current);
      const s = socketRef.current;
      if (s) {
        if (roomRef.current) s.emit("cark_leave_room", { roomCode: roomRef.current });
        s.removeAllListeners();
        s.disconnect();
        socketRef.current = null;
      }
    };
  }, [token]);

  // --- Emit yardımcıları ---
  const broadcastItems = useCallback((nextItems, nextTarget) => {
    if (!isRealtime || !isHost || !socketRef.current) return;
    socketRef.current.emit("cark_update_items", {
      roomCode: roomRef.current,
      items: nextItems,
      targetWinnerCount: nextTarget != null ? nextTarget : targetWinnerCount,
      token,
    });
  }, [isRealtime, isHost, targetWinnerCount, token]);

  // --- Katılımcı yönetimi (host / solo) ---
  const addItem = () => {
    const v = newItem.trim();
    if (!v || spinning) return;
    const next = [...items, v];
    setItems(next);
    setNewItem("");
    broadcastItems(next);
  };
  const removeItem = (i) => {
    if (spinning) return;
    const next = items.filter((_, idx) => idx !== i);
    setItems(next);
    setSelected(new Set()); // index'ler kaydığı için seçimi sıfırla
    setWinners([]);
    broadcastItems(next);
  };

  const changeTarget = (delta) => {
    const nt = Math.max(1, Math.min(items.length - 1 || 1, targetWinnerCount + delta));
    setTargetWinnerCount(nt);
    broadcastItems(items, nt);
  };

  // --- Çevir ---
  const spin = () => {
    if (spinning || !canControl) return;
    const available = items.map((_, i) => i).filter((i) => !selected.has(i));
    if (available.length === 0) return;
    const winningIndex = available[Math.floor(Math.random() * available.length)];

    if (isRealtime && isHost && socketRef.current) {
      // Sunucu herkese (host dahil) cark_spin_started yayınlar -> animasyon oradan tetiklenir
      socketRef.current.emit("cark_spin_start", {
        roomCode: roomRef.current,
        winningIndex,
        targetWinnerCount,
        currentWinnerCount: winners.length,
        winnerName: items[winningIndex],
        token,
      });
    } else {
      // Solo mod
      animateToIndex(winningIndex);
    }
  };

  const resetDraw = () => {
    if (!canControl) return;
    if (isRealtime && isHost && socketRef.current) {
      socketRef.current.emit("cark_reset", { roomCode: roomRef.current, items, token });
    }
    setSelected(new Set());
    setWinners([]);
    setResultWinner(null);
  };

  // --- Canlı oda kontrolü ---
  const startLiveRoom = () => {
    const code = genRoomCode();
    const socket = ensureSocket();
    setRoomCode(code);
    roomRef.current = code;
    setIsHost(true);
    setIsRealtime(true);
    setSelected(new Set());
    setWinners([]);
    const doJoin = () => {
      // Mevcut listeyi odaya it — ama ÖNCE oda sunucuda oluşsun. cark_update_items
      // host kontrolü için cark_rooms satırını okuduğundan, join ack'ini beklemeden
      // gönderirsek "yetkin yok" ile reddedilir ve başlangıç listesi kaybolur.
      socket.once("cark_joined_success", () => {
        socket.emit("cark_update_items", {
          roomCode: code, items, targetWinnerCount, token,
        });
        // Sunucu, oda yeni açıldığı için araya boş bir cark_items_updated
        // gönderip yerel listeyi siliyor; cark_update_items ise gönderene geri
        // yankılanmıyor (socket.to). Odaya ittiğimiz listeyi burada geri koy.
        setItems(items);
        setTargetWinnerCount(targetWinnerCount);
      });
      socket.emit("cark_join_room", { roomCode: code, token });
    };
    if (socket.connected) doJoin();
    else socket.once("connect", doJoin);
  };

  const joinLiveRoom = () => {
    const code = joinInput.trim().toUpperCase();
    if (!code) return;
    const socket = ensureSocket();
    setRoomCode(code);
    roomRef.current = code;
    setIsHost(false);
    setIsRealtime(true);
    setSelected(new Set());
    setWinners([]);
    const doJoin = () => socket.emit("cark_join_room", { roomCode: code, token });
    if (socket.connected) doJoin();
    else socket.once("connect", doJoin);
  };

  const leaveLiveRoom = () => {
    const s = socketRef.current;
    if (s && roomRef.current) s.emit("cark_leave_room", { roomCode: roomRef.current });
    setIsRealtime(false);
    setIsHost(false);
    setRoomCode("");
    roomRef.current = "";
    joinedOnceRef.current = false;
    setUserCount(0);
  };

  // --- Wheel SVG ---
  const n = items.length;
  const seg = n > 0 ? 360 / n : 360;
  const wheelSlices = items.map((label, i) => {
    const a0 = i * seg;
    const a1 = (i + 1) * seg;
    const p0 = polar(a0, RADIUS);
    const p1 = polar(a1, RADIUS);
    const largeArc = seg > 180 ? 1 : 0;
    const d = `M ${CENTER} ${CENTER} L ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} Z`;
    const mid = (a0 + a1) / 2;
    const tp = polar(mid, RADIUS * 0.62);
    let textRot = mid;
    if (mid > 90 && mid < 270) textRot += 180; // alt yarıda ters dönmesin
    const isWon = selected.has(i);
    const short = label.length > 12 ? label.slice(0, 11) + "…" : label;
    return (
      <g key={i} opacity={isWon ? 0.28 : 1}>
        <path d={d} fill={PALETTE[i % PALETTE.length]} stroke="#ffffff" strokeWidth="2" />
        <text
          x={tp.x}
          y={tp.y}
          fill="#ffffff"
          fontSize={n > 16 ? 9 : n > 10 ? 11 : 13}
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`rotate(${textRot.toFixed(2)}, ${tp.x.toFixed(2)}, ${tp.y.toFixed(2)})`}
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {short}
        </text>
      </g>
    );
  });

  const available = items.filter((_, i) => !selected.has(i)).length;

  return (
    <>
      <Header isLoggedIn={Cookies.get("token") != null} />

      <div style={{ minHeight: "70vh", background: "#0f172a", padding: "32px 16px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", color: "#e2e8f0" }}>
          <h1 style={{ textAlign: "center", fontWeight: 800, marginBottom: 6 }}>🎡 Karar Çarkı</h1>
          <p style={{ textAlign: "center", color: "#94a3b8", marginTop: 0 }}>
            {isRealtime
              ? isHost
                ? `Canlı oda (LİDER) — Kod: ${roomCode}`
                : `Canlı izleyici — Oda: ${roomCode}`
              : "Solo mod — çarkı çevir veya canlı oda başlat"}
          </p>

          {errorMsg && (
            <div style={{ background: "#7f1d1d", color: "#fecaca", padding: "10px 16px", borderRadius: 10, textAlign: "center", marginBottom: 12 }}>
              ⚠️ {errorMsg}
            </div>
          )}

          {/* Canlı oda çubuğu */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", alignItems: "center", marginBottom: 20 }}>
            {!isRealtime ? (
              <>
                <button onClick={startLiveRoom} style={btnPrimary}>Canlı Oda Başlat</button>
                <input
                  value={joinInput}
                  onChange={(e) => setJoinInput(e.target.value.toUpperCase())}
                  placeholder="ODA KODU"
                  maxLength={6}
                  style={inputStyle}
                />
                <button onClick={joinLiveRoom} style={btnGhost}>Odaya Katıl</button>
              </>
            ) : (
              <>
                <span style={{ ...badge, background: connected ? "#065f46" : "#7f1d1d" }}>
                  {connected ? "🟢 Bağlı" : "🔴 Bağlantı yok"}
                </span>
                <span style={badge}>👥 {userCount} kişi</span>
                <span style={badge}>Kod: <b>{roomCode}</b></span>
                <button onClick={leaveLiveRoom} style={btnGhost}>Odadan Ayrıl</button>
              </>
            )}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 32, justifyContent: "center" }}>
            {/* Çark */}
            <div style={{ textAlign: "center" }}>
              <div style={{ position: "relative", width: 340, height: 360 }}>
                {/* pointer (tepe) */}
                <svg width="340" height="360" viewBox="0 0 340 360" style={{ position: "absolute", inset: 0 }}>
                  <g transform="translate(10,30)">
                    <g
                      style={{
                        transform: `rotate(${rotation}deg)`,
                        transformOrigin: `${CENTER}px ${CENTER}px`,
                        transition: spinning ? `transform ${SPIN_MS}ms cubic-bezier(0.16, 0.84, 0.30, 1)` : "none",
                      }}
                    >
                      {n > 0 ? wheelSlices : (
                        <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="#334155" />
                      )}
                      <circle cx={CENTER} cy={CENTER} r="26" fill="#0f172a" stroke="#ffffff" strokeWidth="3" />
                    </g>
                  </g>
                  {/* sabit pointer (dönmeyen) */}
                  <polygon points="170,18 156,44 184,44" fill="#facc15" stroke="#0f172a" strokeWidth="2" />
                </svg>
              </div>

              <button
                onClick={spin}
                disabled={spinning || !canControl || available === 0}
                style={{
                  ...btnPrimary,
                  marginTop: 8,
                  minWidth: 200,
                  fontSize: 18,
                  opacity: spinning || !canControl || available === 0 ? 0.5 : 1,
                  cursor: spinning || !canControl || available === 0 ? "not-allowed" : "pointer",
                }}
              >
                {spinning ? "Dönüyor…" : !canControl ? "Lider çeviriyor…" : available === 0 ? "Herkes seçildi" : "🎯 Çarkı Çevir"}
              </button>

              {canControl && (
                <button onClick={resetDraw} disabled={spinning} style={{ ...btnGhost, marginTop: 8, marginLeft: 8 }}>
                  ↺ Sıfırla
                </button>
              )}

              {resultWinner && (
                <div style={{ marginTop: 14, background: "#065f46", color: "#d1fae5", padding: "12px 20px", borderRadius: 12, fontWeight: 800, fontSize: 18 }}>
                  🎉 Kazanan: {resultWinner}
                </div>
              )}
            </div>

            {/* Yan panel */}
            <div style={{ flex: "1 1 300px", minWidth: 280, maxWidth: 380 }}>
              {canControl && (
                <div style={panel}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <input
                      value={newItem}
                      onChange={(e) => setNewItem(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addItem()}
                      placeholder="Katılımcı ekle…"
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button onClick={addItem} style={btnPrimary}>Ekle</button>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span style={{ color: "#94a3b8" }}>Seçilecek kişi:</span>
                    <button onClick={() => changeTarget(-1)} style={btnMini}>−</button>
                    <b style={{ minWidth: 20, textAlign: "center" }}>{targetWinnerCount}</b>
                    <button onClick={() => changeTarget(1)} style={btnMini}>+</button>
                  </div>
                </div>
              )}

              <div style={panel}>
                <h3 style={{ marginTop: 0, fontSize: 16 }}>Katılımcılar ({items.length})</h3>
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {items.map((it, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", borderRadius: 8, background: selected.has(i) ? "#1e293b" : "transparent", textDecoration: selected.has(i) ? "line-through" : "none", color: selected.has(i) ? "#64748b" : "#e2e8f0" }}>
                      <span>
                        <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: PALETTE[i % PALETTE.length], marginRight: 8 }} />
                        {it}
                      </span>
                      {canControl && !spinning && (
                        <button onClick={() => removeItem(i)} style={{ ...btnMini, color: "#f87171" }}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {winners.length > 0 && (
                <div style={panel}>
                  <h3 style={{ marginTop: 0, fontSize: 16 }}>🏆 Kazananlar</h3>
                  <ol style={{ margin: 0, paddingLeft: 20 }}>
                    {winners.map((w, i) => (
                      <li key={i} style={{ padding: "2px 0" }}>{w}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

const btnPrimary = {
  background: "#7c3aed",
  color: "#fff",
  border: "none",
  padding: "10px 18px",
  borderRadius: 10,
  fontWeight: 700,
  cursor: "pointer",
};
const btnGhost = {
  background: "transparent",
  color: "#e2e8f0",
  border: "1px solid #475569",
  padding: "10px 18px",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
};
const btnMini = {
  background: "#1e293b",
  color: "#e2e8f0",
  border: "1px solid #334155",
  width: 30,
  height: 30,
  borderRadius: 8,
  fontWeight: 700,
  cursor: "pointer",
};
const inputStyle = {
  background: "#1e293b",
  color: "#e2e8f0",
  border: "1px solid #334155",
  padding: "10px 12px",
  borderRadius: 10,
  outline: "none",
};
const badge = {
  background: "#1e293b",
  color: "#e2e8f0",
  padding: "6px 12px",
  borderRadius: 20,
  fontSize: 14,
};
const panel = {
  background: "#1e293b",
  borderRadius: 14,
  padding: 16,
  marginBottom: 16,
  border: "1px solid #334155",
};
